/**
 * AM/SA internal-lens card: optional bank metrics, INTERNAL C/D prequal, quote helper, publish.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { customInstance } from "@/lib/axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { staffUi } from "@/pages/applications/staff-ui-classes";

export type BankMonth = {
  month_index: number;
  beginning_balance: number | null;
  ending_balance: number | null;
  number_of_deposits: number | null;
};

export type BankStatementMetrics = {
  months: BankMonth[];
  has_working_capital_loans: boolean | null;
  working_capital_loans: { name: string; balance: number | null; monthly_payment: number | null }[];
  notes: string;
};

export type InternalPrequalRow = {
  lender_id?: number;
  lender_name?: string;
  lender_grade?: string;
  tier_id?: number;
  tier_name?: string;
  matched?: boolean;
  failure_reason?: string;
  failure_code?: string;
  rate_min?: number;
  rate_max?: number;
  term_months?: number[];
  min_down_payment_pct?: number | null;
  payment_range?: { payment_min?: number; payment_max?: number; min?: number; max?: number };
  am_rate?: number;
  am_term_months?: number;
  am_monthly_payment?: number;
  am_down_payment_pct?: number;
  am_notes?: string;
  am_edited?: boolean;
};

type Props = {
  applicationId: string;
  bankMetrics?: BankStatementMetrics | null;
  internalPrequal?: { results?: InternalPrequalRow[]; run_at?: string } | null;
  manualOfferPublish?: {
    source_tier_id?: number;
    dealer_card?: { label?: string };
    published_at?: string;
  } | null;
  equipmentPrice?: number | null;
  requestedAmount?: number | null;
};

function emptyMetrics(): BankStatementMetrics {
  return {
    months: [1, 2, 3].map((i) => ({
      month_index: i,
      beginning_balance: null,
      ending_balance: null,
      number_of_deposits: null,
    })),
    has_working_capital_loans: null,
    working_capital_loans: [],
    notes: "",
  };
}

/** Month N+1 beginning = month N ending. */
function withBalanceContinuity(m: BankStatementMetrics): BankStatementMetrics {
  const months = m.months.map((row) => ({ ...row }));
  for (let i = 1; i < months.length; i++) {
    const prevEnd = months[i - 1]?.ending_balance;
    if (prevEnd != null) {
      months[i] = { ...months[i], beginning_balance: prevEnd };
    }
  }
  return { ...m, months };
}

function numOrEmpty(v: number | null | undefined): string {
  return v == null || Number.isNaN(v) ? "" : String(v);
}

export function InternalCdProgramsCard({
  applicationId,
  bankMetrics,
  internalPrequal,
  manualOfferPublish,
  equipmentPrice,
  requestedAmount,
}: Props) {
  const queryClient = useQueryClient();
  const [metrics, setMetrics] = useState<BankStatementMetrics>(() =>
    withBalanceContinuity(bankMetrics || emptyMetrics())
  );
  const [quoteRate, setQuoteRate] = useState("18");
  const [quoteTerm, setQuoteTerm] = useState("48");
  const [quoteDown, setQuoteDown] = useState("0");
  const [quoteResult, setQuoteResult] = useState<number | null>(null);
  const [editDrafts, setEditDrafts] = useState<Record<number, Partial<InternalPrequalRow>>>({});

  useEffect(() => {
    if (bankMetrics) setMetrics(withBalanceContinuity(bankMetrics));
  }, [bankMetrics]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["application", applicationId] });

  const saveMetricsMutation = useMutation({
    mutationFn: async (body: BankStatementMetrics) => {
      const { data } = await customInstance.patch(
        `/api/applications/${applicationId}/bank-statement-metrics/`,
        body
      );
      return data;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Bank metrics saved");
    },
    onError: (e: any) => toast.error(e?.response?.data?.error ?? "Failed to save bank metrics"),
  });

  const runInternalMutation = useMutation({
    mutationFn: async () => {
      const { data } = await customInstance.post(
        `/api/applications/${applicationId}/run-internal-prequal/`
      );
      return data;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Internal C/D prequal complete");
    },
    onError: (e: any) => toast.error(e?.response?.data?.error ?? "Internal prequal failed"),
  });

  const patchOfferMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await customInstance.patch(
        `/api/applications/${applicationId}/internal-prequal-offers/`,
        payload
      );
      return data;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Offer updated");
    },
    onError: (e: any) => toast.error(e?.response?.data?.error ?? "Failed to update offer"),
  });

  const quoteMutation = useMutation({
    mutationFn: async () => {
      const principal = Number(requestedAmount ?? equipmentPrice ?? 0);
      const { data } = await customInstance.post(`/api/applications/${applicationId}/quote-helper/`, {
        principal,
        down_payment: Number(quoteDown || 0),
        annual_rate_pct: Number(quoteRate || 0),
        term_months: Number(quoteTerm || 0),
        points_pct: 0,
        cadence: "monthly",
      });
      return data;
    },
    onSuccess: (data) => {
      setQuoteResult(data.payment_amount ?? null);
      toast.success("Quote calculated");
    },
    onError: (e: any) => toast.error(e?.response?.data?.error ?? "Quote failed"),
  });

  const publishMutation = useMutation({
    mutationFn: async (tierId: number) => {
      const { data } = await customInstance.post(
        `/api/applications/${applicationId}/manual-offer/publish/`,
        { tier_id: tierId }
      );
      return data;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Manual offer published to dealer view");
    },
    onError: (e: any) => toast.error(e?.response?.data?.error ?? "Publish failed"),
  });

  const unpublishMutation = useMutation({
    mutationFn: async () => {
      const { data } = await customInstance.post(
        `/api/applications/${applicationId}/manual-offer/unpublish/`
      );
      return data;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Manual offer unpublished");
    },
    onError: (e: any) => toast.error(e?.response?.data?.error ?? "Unpublish failed"),
  });

  const results = internalPrequal?.results ?? [];
  const publishedTierId = manualOfferPublish?.source_tier_id;

  const updateMonth = (idx: number, field: keyof BankMonth, raw: string) => {
    setMetrics((prev) => {
      const n = raw.trim() === "" ? null : Number(raw);
      const value = n != null && !Number.isNaN(n) ? n : null;
      const months = prev.months.map((m, i) => {
        if (i !== idx) return m;
        return { ...m, [field]: value };
      });
      // Month N+1 beginning = month N ending (statement continuity).
      if (field === "ending_balance" && idx < months.length - 1) {
        months[idx + 1] = { ...months[idx + 1], beginning_balance: value };
      }
      return { ...prev, months };
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
          <span>Internal programs (C/D)</span>
          {manualOfferPublish?.published_at ? (
            <Badge variant="default">Published to dealer</Badge>
          ) : (
            <Badge variant="secondary">Internal only</Badge>
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground leading-snug">
          Bank metrics are optional. Programs 1–2 require them; Program 3 can match without. Publish one
          structure for the dealer Manual offer card (no rate shown to dealers).
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <p className="text-sm font-medium">Bank statement metrics (3 months)</p>
          <p className="text-xs text-muted-foreground">
            Month 2 beginning = month 1 ending; month 3 beginning = month 2 ending (auto-filled).
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="p-1">Month</th>
                  <th className="p-1">Beginning bal.</th>
                  <th className="p-1">Ending bal.</th>
                  <th className="p-1"># Deposits</th>
                </tr>
              </thead>
              <tbody>
                {metrics.months.map((m, idx) => (
                  <tr key={m.month_index}>
                    <td className="p-1">{m.month_index}</td>
                    <td className="p-1">
                      <Input
                        className="h-8"
                        inputMode="decimal"
                        value={numOrEmpty(m.beginning_balance)}
                        disabled={idx > 0}
                        title={
                          idx > 0
                            ? `Matches month ${idx} ending balance`
                            : undefined
                        }
                        onChange={(e) => updateMonth(idx, "beginning_balance", e.target.value)}
                      />
                    </td>
                    <td className="p-1">
                      <Input
                        className="h-8"
                        inputMode="decimal"
                        value={numOrEmpty(m.ending_balance)}
                        onChange={(e) => updateMonth(idx, "ending_balance", e.target.value)}
                      />
                    </td>
                    <td className="p-1">
                      <Input
                        className="h-8"
                        inputMode="numeric"
                        value={numOrEmpty(m.number_of_deposits)}
                        onChange={(e) => updateMonth(idx, "number_of_deposits", e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Label className="text-sm">Working capital loans?</Label>
            <select
              className="h-8 rounded border px-2 text-sm"
              value={
                metrics.has_working_capital_loans === true
                  ? "yes"
                  : metrics.has_working_capital_loans === false
                    ? "no"
                    : ""
              }
              onChange={(e) => {
                const v = e.target.value;
                setMetrics((prev) => ({
                  ...prev,
                  has_working_capital_loans: v === "yes" ? true : v === "no" ? false : null,
                }));
              }}
            >
              <option value="">—</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div>
            <Label className="text-sm">Notes</Label>
            <Input
              className="mt-1"
              value={metrics.notes || ""}
              onChange={(e) => setMetrics((prev) => ({ ...prev, notes: e.target.value }))}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={saveMetricsMutation.isPending}
              onClick={() => saveMetricsMutation.mutate(metrics)}
            >
              Save bank metrics
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={runInternalMutation.isPending}
              onClick={() => runInternalMutation.mutate()}
            >
              Run internal prequal
            </Button>
            {manualOfferPublish?.published_at ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={unpublishMutation.isPending}
                onClick={() => unpublishMutation.mutate()}
              >
                Unpublish Manual offer
              </Button>
            ) : null}
          </div>
        </div>

        <div className={staffUi.staffOnlyPanel}>
          <p className={staffUi.staffOnlyEyebrow}>Quote helper</p>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">Rate %</Label>
              <Input className="h-8" value={quoteRate} onChange={(e) => setQuoteRate(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Term (mo)</Label>
              <Input className="h-8" value={quoteTerm} onChange={(e) => setQuoteTerm(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Down $</Label>
              <Input className="h-8" value={quoteDown} onChange={(e) => setQuoteDown(e.target.value)} />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                size="sm"
                className="w-full"
                disabled={quoteMutation.isPending}
                onClick={() => quoteMutation.mutate()}
              >
                Calculate
              </Button>
            </div>
          </div>
          {quoteResult != null && (
            <p className="mt-2 text-sm">
              Monthly payment: <span className="font-medium">${quoteResult.toFixed(2)}</span>
            </p>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">
            Internal results{internalPrequal?.run_at ? ` · ${new Date(internalPrequal.run_at).toLocaleString()}` : ""}
          </p>
          {results.length === 0 ? (
            <p className="text-sm text-muted-foreground">No internal run yet.</p>
          ) : (
            results.map((r) => {
              const tid = r.tier_id!;
              const draft = editDrafts[tid] || {};
              const rate = draft.am_rate ?? r.am_rate ?? r.rate_min;
              const term = draft.am_term_months ?? r.am_term_months ?? (r.term_months || [])[0];
              const pmt =
                draft.am_monthly_payment ??
                r.am_monthly_payment ??
                r.payment_range?.payment_min ??
                r.payment_range?.min;
              const down = draft.am_down_payment_pct ?? r.am_down_payment_pct ?? r.min_down_payment_pct;
              const isPublished = publishedTierId === tid;
              return (
                <div key={tid} className={r.matched ? staffUi.offerRowSelected : staffUi.offerRow}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">
                      {r.lender_name}
                      {r.lender_grade ? ` (${r.lender_grade})` : ""} — {r.tier_name}
                    </span>
                    <div className="flex gap-1.5">
                      {r.matched ? <Badge>Matched</Badge> : <Badge variant="secondary">Unmatched</Badge>}
                      {isPublished ? <Badge variant="default">Published</Badge> : null}
                      {r.am_edited ? <Badge variant="outline">Edited</Badge> : null}
                    </div>
                  </div>
                  {!r.matched && r.failure_reason ? (
                    <p className="text-xs text-muted-foreground mt-1">{r.failure_reason}</p>
                  ) : null}
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-xs">Rate %</Label>
                      <Input
                        className="h-8"
                        value={numOrEmpty(rate ?? null)}
                        onChange={(e) =>
                          setEditDrafts((d) => ({
                            ...d,
                            [tid]: { ...d[tid], am_rate: e.target.value === "" ? undefined : Number(e.target.value) },
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Term</Label>
                      <Input
                        className="h-8"
                        value={numOrEmpty(term ?? null)}
                        onChange={(e) =>
                          setEditDrafts((d) => ({
                            ...d,
                            [tid]: {
                              ...d[tid],
                              am_term_months: e.target.value === "" ? undefined : Number(e.target.value),
                            },
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Payment $</Label>
                      <Input
                        className="h-8"
                        value={numOrEmpty(pmt ?? null)}
                        onChange={(e) =>
                          setEditDrafts((d) => ({
                            ...d,
                            [tid]: {
                              ...d[tid],
                              am_monthly_payment: e.target.value === "" ? undefined : Number(e.target.value),
                            },
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Down %</Label>
                      <Input
                        className="h-8"
                        value={numOrEmpty(down ?? null)}
                        onChange={(e) =>
                          setEditDrafts((d) => ({
                            ...d,
                            [tid]: {
                              ...d[tid],
                              am_down_payment_pct: e.target.value === "" ? undefined : Number(e.target.value),
                            },
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={patchOfferMutation.isPending}
                      onClick={() =>
                        patchOfferMutation.mutate({
                          tier_id: tid,
                          rate: editDrafts[tid]?.am_rate ?? rate,
                          term_months: editDrafts[tid]?.am_term_months ?? term,
                          monthly_payment: editDrafts[tid]?.am_monthly_payment ?? pmt,
                          down_payment_pct: editDrafts[tid]?.am_down_payment_pct ?? down,
                        })
                      }
                    >
                      Save edits
                    </Button>
                    {(r.matched || r.am_edited) && (
                      <Button
                        type="button"
                        size="sm"
                        disabled={publishMutation.isPending}
                        onClick={() => publishMutation.mutate(tid)}
                      >
                        Publish as Manual offer
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
